import { useState, useEffect } from "react";
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import axios from "axios";
import MDBox from "admin/components/MDBox";
import MDTypography from "admin/components/MDTypography";
import DashboardLayout from "admin/examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "admin/examples/Navbars/DashboardNavbar";
import { BarChart, Bar, LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from 'recharts';

const API_URL = process.env.REACT_APP_APIHOST || "http://localhost:3033";

function TrafficObserver() {
    const [stats, setStats] = useState(null);
    const [visits, setVisits] = useState([]);
    const [loading, setLoading] = useState(true);
    const [quoteStats, setQuoteStats] = useState(null);

    useEffect(() => {
        fetchData();
    }, []);

    const fetchData = async () => {
        try {
            const [statsRes, visitsRes, quoteRes] = await Promise.all([
                axios.get(`${API_URL}/track/stats`),
                axios.get(`${API_URL}/track/visits?limit=100`),
                axios.get(`${API_URL}/track/quote-stats`),
            ]);
            if (statsRes.data.status) setStats(statsRes.data.data);
            if (visitsRes.data.status) setVisits(visitsRes.data.data);
            if (quoteRes.data.status) setQuoteStats(quoteRes.data.data);
        } catch (e) { console.error(e); }
        finally { setLoading(false); }
    };

    const formatDuration = (s) => {
        if (!s || s < 1) return "< 1s";
        if (s < 60) return Math.round(s) + "s";
        return Math.round(s / 60) + "m " + Math.round(s % 60) + "s";
    };

    const parseUA = (ua) => {
        if (!ua) return "Unknown";
        if (ua.includes("Mobile")) return "Mobile";
        if (ua.includes("Chrome")) return "Chrome";
        if (ua.includes("Firefox")) return "Firefox";
        if (ua.includes("Safari")) return "Safari";
        return "Other";
    };

    if (loading) return (
        <DashboardLayout><DashboardNavbar />
            <MDBox py={3} display="flex" justifyContent="center"><div className="forte-spin"></div></MDBox>
        </DashboardLayout>
    );

    return (
        <DashboardLayout>
            <DashboardNavbar />
            <MDBox py={2}>
                {/* KPIs */}
                <Grid container spacing={2} mb={2}>
                    {[
                        { label: "Today views", value: stats?.today?.views || 0, icon: "pageview" },
                        { label: "Today sessions", value: stats?.today?.sessions || 0, icon: "person" },
                        { label: "Unique IPs (24h)", value: stats?.today?.uniqueIps || 0, icon: "fingerprint" },
                        { label: "Month views", value: stats?.month?.views || 0, icon: "calendar_today" },
                        { label: "Avg duration", value: formatDuration(stats?.avgDuration), icon: "timer" },
                    ].map((kpi, i) => (
                        <Grid item xs={6} lg={3} key={i}>
                            <MDBox sx={{ background: "#222", border: "1px solid rgba(255,255,255,0.06)", borderRadius: "2px", p: 2 }}>
                                <MDTypography sx={{ color: "#C29717", fontSize: "9px", textTransform: "uppercase", letterSpacing: "1.5px", mb: 0.5 }}>{kpi.label}</MDTypography>
                                <MDTypography sx={{ color: "#fff", fontSize: "24px", fontWeight: 400 }}>{kpi.value}</MDTypography>
                            </MDBox>
                        </Grid>
                    ))}
                </Grid>

                {/* Chart */}
                <Card sx={{ p: 2, mb: 2 }}>
                    <MDTypography sx={{ color: "#C29717", fontSize: "10px", textTransform: "uppercase", letterSpacing: "1.5px", mb: 2 }}>Daily traffic</MDTypography>
                    {stats?.dailyViews?.length > 0 ? (
                        <ResponsiveContainer width="100%" height={250}>
                            <BarChart data={stats.dailyViews} margin={{ top: 5, right: 5, left: 0, bottom: 0 }}>
                                <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.04)" />
                                <XAxis dataKey="_id" tick={{ fill: '#888', fontSize: 10 }} tickFormatter={d => d.slice(5)} />
                                <YAxis tick={{ fill: '#888', fontSize: 10 }} />
                                <Tooltip contentStyle={{ background: '#1e1e1e', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '2px', fontSize: '11px', color: '#ccc' }} />
                                <Bar dataKey="views" name="Views" fill="rgba(194,151,23,0.6)" radius={[2,2,0,0]} />
                                <Bar dataKey="sessions" name="Sessions" fill="rgba(255,255,255,0.15)" radius={[2,2,0,0]} />
                            </BarChart>
                        </ResponsiveContainer>
                    ) : <MDTypography sx={{ color: "#666", fontSize: "12px" }}>No data yet</MDTypography>}
                </Card>

                {/* Pages + Actions breakdown */}
                <Grid container spacing={2} mb={2}>
                    <Grid item xs={12} md={6}>
                        <Card sx={{ p: 2 }}>
                            <MDTypography sx={{ color: "#C29717", fontSize: "10px", textTransform: "uppercase", letterSpacing: "1.5px", mb: 1 }}>Pages (this month)</MDTypography>
                            {(stats?.pageBreakdown || []).map(p => (
                                <MDBox key={p._id} display="flex" justifyContent="space-between" sx={{ py: 0.6, borderBottom: "1px solid rgba(255,255,255,0.03)" }}>
                                    <MDTypography sx={{ color: "#ccc", fontSize: "12px" }}>{p._id}</MDTypography>
                                    <MDTypography sx={{ color: "#fff", fontSize: "12px" }}>{p.count}</MDTypography>
                                </MDBox>
                            ))}
                        </Card>
                    </Grid>
                    <Grid item xs={12} md={6}>
                        <Card sx={{ p: 2 }}>
                            <MDTypography sx={{ color: "#C29717", fontSize: "10px", textTransform: "uppercase", letterSpacing: "1.5px", mb: 1 }}>Actions (this month)</MDTypography>
                            {(stats?.actionBreakdown || []).map(a => (
                                <MDBox key={a._id} display="flex" justifyContent="space-between" sx={{ py: 0.6, borderBottom: "1px solid rgba(255,255,255,0.03)" }}>
                                    <MDTypography sx={{ color: "#ccc", fontSize: "12px" }}>{a._id}</MDTypography>
                                    <MDTypography sx={{ color: "#fff", fontSize: "12px" }}>{a.count}</MDTypography>
                                </MDBox>
                            ))}
                        </Card>
                    </Grid>
                </Grid>

                {/* Quote Views */}
                {quoteStats && (
                <Card sx={{ p: 2, mb: 2 }}>
                    <MDTypography sx={{ color: "#C29717", fontSize: "10px", textTransform: "uppercase", letterSpacing: "1.5px", mb: 1 }}>Quote Views</MDTypography>
                    {(quoteStats.summary || []).map(q => (
                        <MDBox key={q.v} display="flex" justifyContent="space-between" alignItems="center" sx={{ py: 0.8, borderBottom: "1px solid rgba(255,255,255,0.04)" }}>
                            <MDBox>
                                <MDTypography sx={{ color: "#fff", fontSize: "13px", fontWeight: 500 }}>V={q.v}</MDTypography>
                                <MDTypography sx={{ color: "#666", fontSize: "10px" }}>{q.uniqueIps} unique IP{q.uniqueIps !== 1 ? "s" : ""}</MDTypography>
                            </MDBox>
                            <MDBox display="flex" alignItems="center" gap={2}>
                                <MDTypography sx={{ color: "#C29717", fontSize: "16px", fontWeight: 500 }}>{q.count}</MDTypography>
                                <MDTypography sx={{ color: "#666", fontSize: "10px", whiteSpace: "nowrap" }}>
                                    {q.lastSeen ? new Date(q.lastSeen).toLocaleString("en-GB", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" }) : ""}
                                </MDTypography>
                            </MDBox>
                        </MDBox>
                    ))}
                    {(quoteStats.recentViews || []).length > 0 && (
                        <MDBox mt={2}>
                            <MDTypography sx={{ color: "#C29717", fontSize: "9px", textTransform: "uppercase", letterSpacing: "1.5px", mb: 0.5 }}>Recent hits</MDTypography>
                            {(quoteStats.recentViews || []).slice(0, 20).map((v, i) => (
                                <MDBox key={i} display="flex" justifyContent="space-between" sx={{ py: 0.4, borderBottom: "1px solid rgba(255,255,255,0.02)" }}>
                                    <MDBox display="flex" gap={1} alignItems="center">
                                        <span style={{ fontSize: "10px", color: "#C29717", background: "rgba(194,151,23,0.08)", border: "1px solid rgba(194,151,23,0.2)", padding: "1px 6px", borderRadius: "2px" }}>V={v.v}</span>
                                        <span style={{ fontSize: "10px", color: "#888" }}>{v.ip}</span>
                                    </MDBox>
                                    <span style={{ fontSize: "10px", color: "#666" }}>
                                        {v.timestamp ? new Date(v.timestamp).toLocaleString("en-GB", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" }) : ""}
                                    </span>
                                </MDBox>
                            ))}
                        </MDBox>
                    )}
                </Card>
                )}

                {/* Recent visits */}
                <Card sx={{ p: 2 }}>
                    <MDTypography sx={{ color: "#C29717", fontSize: "10px", textTransform: "uppercase", letterSpacing: "1.5px", mb: 1 }}>Recent visits</MDTypography>
                    {visits.map(v => (
                        <MDBox key={v._id} sx={{ borderBottom: "1px solid rgba(255,255,255,0.04)", py: 1 }}>
                            <MDBox display="flex" justifyContent="space-between" alignItems="flex-start">
                                <MDBox>
                                    <MDBox display="flex" alignItems="center" gap={1} mb={0.3}>
                                        <MDTypography sx={{ color: "#fff", fontSize: "12px" }}>{v.ip}</MDTypography>
                                        {v.userEmail && <span style={{ fontSize: "10px", color: "#C29717", background: "rgba(194,151,23,0.08)", border: "1px solid rgba(194,151,23,0.2)", padding: "1px 6px", borderRadius: "2px" }}>{v.userEmail}</span>}
                                    </MDBox>
                                    <MDBox display="flex" gap={1} flexWrap="wrap" alignItems="center">
                                        <span style={{ fontSize: "10px", color: "#888" }}>{parseUA(v.userAgent)}</span>
                                        <span style={{ fontSize: "10px", color: "#666" }}>{v.pageviews} pages</span>
                                        <span style={{ fontSize: "10px", color: "#666" }}>{v.actions} actions</span>
                                        <span style={{ fontSize: "10px", color: "#C29717" }}>{formatDuration(v.duration)}</span>
                                    </MDBox>
                                    <MDBox display="flex" gap={0.5} mt={0.3} flexWrap="wrap">
                                        {(v.pages || []).map(p => (
                                            <span key={p} style={{ fontSize: "9px", color: "#888", background: "rgba(255,255,255,0.04)", padding: "0 4px", borderRadius: "2px" }}>{p}</span>
                                        ))}
                                    </MDBox>
                                </MDBox>
                                <MDTypography sx={{ color: "#666", fontSize: "10px", whiteSpace: "nowrap" }}>
                                    {v.end ? new Date(v.end).toLocaleString("en-GB", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" }) : ""}
                                </MDTypography>
                            </MDBox>
                        </MDBox>
                    ))}
                </Card>
            </MDBox>
        </DashboardLayout>
    );
}

export default TrafficObserver;
